import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from '../../hooks/use-toast';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Money } from '../ui/Money';
import { Plane, Plus, Minus, Settings2, CalendarClock, Trash2, ArrowRight } from 'lucide-react';
import { TicketRecordDialog } from '../tickets/TicketRecordDialog';
import { AddTicketRecordDialog } from '../tickets/AddTicketRecordDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLES = {
  overdue: 'bg-rose-100 text-rose-700',
  urgent: 'bg-rose-50 text-rose-600',
  warning: 'bg-amber-50 text-amber-700',
  info: 'bg-sky-50 text-sky-700',
};

const daysLabel = (d) => {
  if (d == null) return '—';
  if (d < 0) return `${Math.abs(d)}d overdue`;
  if (d === 0) return 'Today';
  return `in ${d}d`;
};

const TicketReminderCard = ({ compact = false }) => {
  const [summary, setSummary] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [records, setRecords] = useState([]);
  const [year, setYear] = useState(null);
  const [leadDays, setLeadDays] = useState([60, 45, 30]);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [leadInput, setLeadInput] = useState('60, 45, 30');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async (yr) => {
    try {
      const [sumRes, remRes, recRes] = await Promise.all([
        axios.get(`${API}/api/ticket-tracker/cost-summary${yr ? `?year=${yr}` : ''}`, { headers }),
        axios.get(`${API}/api/ticket-tracker/reminders${yr ? `?year=${yr}` : ''}`, { headers }),
        axios.get(`${API}/api/ticket-tracker/records${yr ? `?year=${yr}` : ''}`, { headers }),
      ]);
      setSummary(sumRes.data);
      if (yr == null) setYear(sumRes.data.year);
      setReminders(remRes.data.reminders || []);
      setRecords(recRes.data.records || []);
      setLeadDays(remRes.data.lead_days || [60, 45, 30]);
      setLeadInput((remRes.data.lead_days || [60, 45, 30]).join(', '));
    } catch (err) {
      console.error('Ticket reminder load failed', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(null); }, [load]);

  const onYearChange = (yr) => { setYear(Number(yr)); load(Number(yr)); };

  const saveLeadDays = async () => {
    const parsed = leadInput.split(',').map((x) => parseInt(x.trim(), 10)).filter((n) => n > 0);
    if (parsed.length === 0) {
      toast({ title: 'Invalid', description: 'Enter at least one positive number', variant: 'destructive' });
      return;
    }
    try {
      await axios.put(`${API}/api/ticket-tracker/reminder-settings`, { lead_days: parsed }, { headers });
      toast({ title: 'Saved', description: 'Reminder window updated' });
      setShowSettings(false);
      load(year);
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Could not save', variant: 'destructive' });
    }
  };

  const diff = summary?.difference ?? 0;
  const issuedRecords = records.filter((r) => r.status === 'issued');

  const deleteRecord = async (rec) => {
    if (!window.confirm(`Delete this ticket record for ${rec.employee_name}?`)) return;
    try {
      await axios.delete(`${API}/api/ticket-tracker/record/${rec.id}`, { headers });
      toast({ title: 'Deleted', description: 'Ticket record removed.' });
      load(year);
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Could not delete', variant: 'destructive' });
    }
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="ticket-reminder-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Plane className="h-5 w-5 text-sky-600" />
            <h3 className="text-lg font-semibold text-gray-900">Ticket Due Reminder</h3>
            <Badge variant="outline" className="text-xs">{reminders.length} due</Badge>
          </div>
          <div className="flex items-center gap-2">
            {!compact && (
              <Button size="sm" onClick={() => setShowAdd(true)} data-testid="ticket-add-record-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Record
              </Button>
            )}
            {summary?.available_years?.length > 0 && (
              <Select value={String(year || '')} onValueChange={onYearChange}>
                <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="ticket-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {summary.available_years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {compact ? (
              <Link to="/ticket-reminders">
                <Button variant="outline" size="sm" data-testid="ticket-view-details-btn">
                  View details <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setShowSettings((s) => !s)} data-testid="ticket-settings-btn">
                <Settings2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {!compact && showSettings && (
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 flex items-end gap-2 flex-wrap" data-testid="ticket-settings-panel">
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                <CalendarClock className="h-3 w-3" /> Remind these many days before leave starts
              </label>
              <Input value={leadInput} onChange={(e) => setLeadInput(e.target.value)} placeholder="60, 45, 30" data-testid="ticket-lead-input" />
            </div>
            <Button size="sm" onClick={saveLeadDays} data-testid="ticket-lead-save">Save</Button>
          </div>
        )}

        {/* Cost summary widget */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <div className="rounded-xl bg-violet-50 p-3" data-testid="ticket-widget-due">
            <p className="text-[10px] uppercase text-violet-500 font-semibold">Upcoming Dues</p>
            <p className="text-2xl font-bold text-violet-700">{summary?.upcoming_due_count ?? 0}</p>
          </div>
          <div className="rounded-xl bg-sky-50 p-3" data-testid="ticket-widget-approved">
            <p className="text-[10px] uppercase text-sky-500 font-semibold">Approved Cost</p>
            <p className="text-xl font-bold text-sky-700"><Money value={summary?.approved_total} digits={0} /></p>
          </div>
          <div className="rounded-xl bg-slate-50 p-3" data-testid="ticket-widget-actual">
            <p className="text-[10px] uppercase text-slate-500 font-semibold">Actual Cost</p>
            <p className="text-xl font-bold text-slate-700"><Money value={summary?.actual_total} digits={0} /></p>
          </div>
          <div className={`rounded-xl p-3 ${diff >= 0 ? 'bg-emerald-50' : 'bg-rose-50'}`} data-testid="ticket-widget-savings">
            <p className={`text-[10px] uppercase font-semibold ${diff >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
              {diff >= 0 ? 'Total Saved' : 'Total Excess'}
            </p>
            <p className={`text-xl font-bold inline-flex items-center gap-1 ${diff >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
              {diff > 0 ? <Plus className="h-4 w-4" /> : diff < 0 ? <Minus className="h-4 w-4" /> : null}
              <Money value={Math.abs(diff)} digits={0} />
            </p>
          </div>
        </div>

        {/* Reminder list */}
        {!compact && (reminders.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-4 text-center" data-testid="ticket-reminders-empty">
            No company-paid tickets due within the reminder window.
          </p>
        ) : (
          <div className="space-y-2 max-h-[340px] overflow-y-auto" data-testid="ticket-reminders-list">
            {reminders.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2 hover:bg-gray-50" data-testid={`ticket-reminder-${r.employee_code}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {r.employee_name} <span className="font-mono text-xs text-gray-400">{r.employee_code}</span>
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {r.department || '—'} · {r.leave_start_date ? `Leave ${r.leave_start_date}` : (r.issue_due_date ? `Due ${r.issue_due_date}` : 'No date')}
                    {r.manual && <span className="ml-1 text-[10px] text-gray-400">(manual)</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={`text-[10px] border-0 ${TIER_STYLES[r.tier] || TIER_STYLES.info}`}>
                    {daysLabel(r.days_until_leave)}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`ticket-record-btn-${r.employee_code}`}>
                    Record
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* Issued tickets for the selected year — the history behind the totals */}
        {!compact && issuedRecords.length > 0 && (
          <div className="mt-5 pt-4 border-t border-gray-100" data-testid="ticket-issued-list">
            <p className="text-xs font-semibold uppercase text-gray-400 mb-2">Issued tickets · {year}</p>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {issuedRecords.map((r) => {
                const d = r.fare?.difference;
                return (
                  <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2" data-testid={`ticket-issued-${r.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {r.employee_name} <span className="font-mono text-xs text-gray-400">{r.employee_code}</span>
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        Issued {r.actual_issue_date || '—'}
                        {r.approved_fare != null && <> · Appr <Money value={r.approved_fare} digits={0} /></>}
                        {r.actual_fare != null && <> · Act <Money value={r.actual_fare} digits={0} /></>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {d != null && (
                        <span className={`text-xs font-semibold inline-flex items-center gap-0.5 ${d > 0 ? 'text-emerald-600' : d < 0 ? 'text-rose-600' : 'text-gray-500'}`}>
                          {d > 0 ? <Plus className="h-3 w-3" /> : d < 0 ? <Minus className="h-3 w-3" /> : null}
                          <Money value={Math.abs(d)} digits={0} />
                        </span>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`ticket-edit-issued-${r.id}`}>Edit</Button>
                      <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => deleteRecord(r)} data-testid={`ticket-delete-${r.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>

      {!compact && (
        <>
          <AddTicketRecordDialog
            open={showAdd}
            onOpenChange={setShowAdd}
            onSaved={() => { setShowAdd(false); load(year); }}
          />

          <TicketRecordDialog
            record={editing}
            open={!!editing}
            onOpenChange={(o) => { if (!o) setEditing(null); }}
            onSaved={() => { setEditing(null); load(year); }}
          />
        </>
      )}
    </Card>
  );
};

export default TicketReminderCard;
